import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  LinearProgress,
  IconButton,
  Paper,
  styled,
} from "@mui/material";
import { Poll, Add, Close, Stop, Casino } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useCreatePoll,
  useEndPoll,
  useGetPoll,
} from "../../services/stagemode_api/talent/talent_api";
import { PollDataRequest } from "../../types/stage-mode";
import { enqueueSnackbar } from "notistack";
import SocketHandler from "../../utils/socketHandler";

// Design system tokens — aligned with Sing4You / s4y-* CSS variables
const NLX = {
  surface: "rgba(255,255,255,0.03)",
  surfaceAlt: "rgba(255,255,255,0.05)",
  border: "rgba(255,255,255,0.09)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.5)",
  red: "#f97316",
  redHover: "#ea6a0e",
  redActive: "#d05c08",
};

// Root card
const PollsContainer = styled(Paper)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 12,
  background: "transparent",
  border: "none",
  boxShadow: "none",
}));

// Header (hidden — header now lives in the sidebar tool-tab-header)
const TitleContainer = styled(Box)(({ theme }) => ({
  display: "none",
}));

// Body/scroll
const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: 14,
  "&::-webkit-scrollbar": { width: 3 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(249,115,22,0.25)",
    borderRadius: 4,
  },
}));

// Primary action button
const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontWeight: 800,
  letterSpacing: "0.04em",
  fontSize: "0.82rem",
  padding: "9px 16px",
  backgroundColor: NLX.red,
  color: "#fff",
  border: "1px solid rgba(0,0,0,0.2)",
  boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
  transition: "transform .12s ease, background-color .12s ease, box-shadow .12s ease",
  "&:hover": {
    backgroundColor: NLX.redHover,
    transform: "translateY(-1px)",
    boxShadow: "0 8px 20px rgba(249,115,22,0.35)",
  },
  "&:active": {
    backgroundColor: NLX.redActive,
    transform: "translateY(0)",
    boxShadow: "0 3px 8px rgba(0,0,0,0.4)",
  },
  "&:disabled": {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.35)",
    border: `1px solid ${NLX.border}`,
    boxShadow: "none",
  },
  "&:focus-visible": {
    outline: `2px solid ${NLX.red}`,
    outlineOffset: 2,
  },
}));

// Poll card container
const PollCard = styled(Box)(({ theme }) => ({
  padding: 14,
  marginTop: 14,
  borderRadius: 12,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 100%)",
  border: `1px solid ${NLX.border}`,
}));

// Choice row
const PollOption = styled(Box)(({ theme }) => ({
  padding: "10px 12px",
  marginBottom: 10,
  borderRadius: 10,
  backgroundColor: NLX.surfaceAlt,
  border: `1px solid ${NLX.border}`,
  color: NLX.text,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  transition: "transform .12s ease, background-color .12s ease, border-color .12s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderColor: "rgba(255,255,255,0.2)",
    transform: "translateY(-1px)",
  },
}));

// Result bar
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 999,
  backgroundColor: "rgba(255,255,255,0.07)",
  "& .MuiLinearProgress-bar": {
    borderRadius: 999,
    background: "linear-gradient(90deg, #f97316, #fbbf24)",
  },
}));

// Dialog shell
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    padding: 16,
    minWidth: 380,
    background: NLX.surface,
    color: NLX.text,
    border: `1px solid ${NLX.border}`,
    boxShadow: "0 18px 40px rgba(0,0,0,0.6)",
    [theme.breakpoints.down("sm")]: {
      minWidth: "92%",
      margin: 8,
      padding: 12,
    },
  },
}));

// Text field
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.04)",
    color: NLX.text,
    fontSize: "0.82rem",
    "& fieldset": { borderColor: NLX.border },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
    "&.Mui-focused fieldset": { borderColor: NLX.red },
  },
  "& .MuiOutlinedInput-input": {
    color: NLX.text,
    "&::placeholder": { color: NLX.textMuted },
  },
  "& .MuiInputLabel-root": {
    color: NLX.textMuted,
    "&.Mui-focused": { color: NLX.red },
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(251,191,36,0.8)",
  },
}));


interface PollFormData {
  title: string;
  choices: string[];
}

const initialPollForm: PollFormData = {
  title: "",
  choices: ["", ""],
};

const validationSchema = Yup.object({
  title: Yup.string()
    .required("Poll title is required")
    .max(100, "Title must not exceed 100 characters"),
  choices: Yup.array()
    .of(Yup.string().required("Choice cannot be empty"))
    .min(2, "At least 2 choices are required"),
});

interface LivePollsProps {
  bookingId: string;
  height?: string | number;
}

const LivePolls: React.FC<LivePollsProps> = ({ bookingId, height = "100%" }) => {
  const [pollDialogOpen, setPollDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEndingPoll, setIsEndingPoll] = useState(false);

  //const { mutate: createPoll } = useCreatePoll();
  //const { mutate: endPoll } = useEndPoll();
  const { mutateAsync: createPoll } = useCreatePoll();
const { mutateAsync: endPoll } = useEndPoll();

  const { data: polls, refetch: refetchPoll } = useGetPoll(bookingId);

  // Find active poll from the list
  const activePoll = polls?.data?.find((poll) => poll.status === "Open");

  useEffect(() => {
    if (!bookingId) return;

    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToBooking(bookingId, (message) => {
      if (message?.type === "PollVote") {
        refetchPoll();
      }
    });

    return () => {
      socketHandler.unsubscribeFromBooking(bookingId);
    };
  }, [bookingId, refetchPoll]);

  const formik = useFormik({
    initialValues: initialPollForm,
    validationSchema,
   onSubmit: async (values) => {
  setIsSubmitting(true);
  try {
    const pollData: PollDataRequest = { title: values.title, choices: values.choices };
    await createPoll({ bookingId, data: pollData }); // <-- await
    await refetchPoll();
    setPollDialogOpen(false);
    formik.resetForm();
  } catch (err) {
    enqueueSnackbar("Failed to create poll", { variant: "error" });
  } finally {
    setIsSubmitting(false);
  }
}
  });

  const handleAddChoice = () => {
    formik.setFieldValue("choices", [...formik.values.choices, ""]);
  };

  const handleRemoveChoice = (index: number) => {
    formik.setFieldValue(
      "choices",
      formik.values.choices.filter((_, i) => i !== index)
    );
  };

  const handleCloseDialog = () => {
    setPollDialogOpen(false);
    formik.resetForm();
  };

const handleEndPoll = async () => {
  if (!activePoll) return;
  setIsEndingPoll(true);
  try {
    await endPoll({ bookingId, pollId: String(activePoll.id) }); // <-- await
    await refetchPoll();
  } catch {
    enqueueSnackbar("Failed to end poll", { variant: "error" });
  } finally {
    setIsEndingPoll(false);
  }
};

  if (!bookingId) {
    return (
      <div className="s4y-tool-empty">
        <div className="s4y-tool-empty-icon">📊</div>
        <div className="s4y-tool-empty-title">Live Polls</div>
        <div className="s4y-tool-empty-text">Start a live session to create and manage audience polls.</div>
      </div>
    );
  }

  return (
    <PollsContainer sx={{ height }}>
    
      <ContentContainer>
        <StyledButton
          variant="contained"
          fullWidth
          onClick={() => setPollDialogOpen(true)}
          disabled={!!activePoll}
          startIcon={<Add />}
          sx={{ mb: 2 }}
        >
          Create New Poll
        </StyledButton>

        {/* Active Poll Display */}
        {activePoll && (
          <PollCard>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Box flex={1}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: "#fff",
                    fontSize: "0.875rem",
                    lineHeight: 1.2,
                  }}
                >
                  {activePoll.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.35)",
                    fontSize: "0.7rem",
                    letterSpacing: "0.04em",
                  }}
                >
                  Poll #{activePoll.id}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Stop fontSize="small" />}
                onClick={handleEndPoll}
                disabled={isEndingPoll}
                sx={{
                  borderRadius: 8,
                  textTransform: "none",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  minWidth: "auto",
                  borderColor: "rgba(249,115,22,0.5)",
                  color: "var(--s4y-orange)",
                  "&:hover": {
                    borderColor: "var(--s4y-orange)",
                    backgroundColor: "rgba(249,115,22,0.1)",
                  },
                }}
              >
                {isEndingPoll ? "Ending…" : "End"}
              </Button>
            </Box>
            <Stack spacing={1}>
              {activePoll.options?.map((option) => (
                <PollOption key={option.id}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Casino
                        sx={{ color: "var(--s4y-orange)", fontSize: 16 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#fff",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {option.option}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(249,115,22,0.8)",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {option.votes} votes
                    </Typography>
                  </Box>
                  <StyledLinearProgress
                    variant="determinate"
                    value={
                      option.votes
                        ? (option.votes /
                            activePoll.options.reduce(
                              (sum, opt) => sum + opt.votes,
                              0
                            )) *
                          100
                        : 0
                    }
                  />
                </PollOption>
              ))}
            </Stack>
          </PollCard>
        )}

        {/* No Active Poll Message */}
        {!activePoll && (
          <div className="s4y-tool-empty" style={{ minHeight: 160 }}>
            <div className="s4y-tool-empty-icon">📊</div>
            <div className="s4y-tool-empty-title">No active poll</div>
            <div className="s4y-tool-empty-text">Tap "New Poll" above to engage your audience right now.</div>
          </div>
        )}
      </ContentContainer>

      {/* Poll Creation Dialog */}
      <StyledDialog open={pollDialogOpen} onClose={handleCloseDialog}>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: "#fff",
                fontSize: "1.1rem",
              }}
            >
              Create New Poll
            </Typography>
          </DialogTitle>
          <DialogContent>
            <StyledTextField
              margin="dense"
              label="Poll Title"
              fullWidth
              {...formik.getFieldProps("title")}
              error={!!formik?.touched?.title && !!formik?.errors?.title}
              helperText={formik?.touched?.title && formik?.errors?.title}
              sx={{ mb: 2 }}
            />
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "#fff",
                fontSize: "0.875rem",
                mb: 1,
              }}
            >
              Poll Choices
            </Typography>
            {formik.values.choices.map((choice, index) => (
              <Box key={index} display="flex" alignItems="center" mb={1}>
                <StyledTextField
                  margin="dense"
                  label={`Choice ${index + 1}`}
                  fullWidth
                  name={`choices.${index}`}
                  value={choice}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: (
                      <Casino sx={{ mr: 1, color: "var(--s4y-orange)", fontSize: 18, opacity: 0.7 }} />
                    ),
                  }}
                />
                {index > 1 && (
                  <IconButton
                    onClick={() => handleRemoveChoice(index)}
                    sx={{
                      ml: 1,
                      color: "rgba(255,255,255,0.35)",
                      "&:hover": { color: "var(--s4y-orange)", backgroundColor: "rgba(249,115,22,0.1)" },
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<Add />}
              onClick={handleAddChoice}
              sx={{
                mt: 1,
                color: "var(--s4y-orange)",
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 700,
                "&:hover": { backgroundColor: "rgba(249,115,22,0.08)" },
              }}
            >
              Add Choice
            </Button>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: "rgba(255,255,255,0.45)",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)" },
              }}
            >
              Cancel
            </Button>
            <StyledButton
              type="submit"
              variant="contained"
              disabled={isSubmitting || !formik.isValid || formik.isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Launch Poll"}
            </StyledButton>
          </DialogActions>
        </form>
      </StyledDialog>
    </PollsContainer>
  );
};

export default LivePolls;